import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

import { Box, Button, Card, Skeleton, styled, Typography } from '@mui/material';

import { urls } from 'Router';
import { Colors } from 'Themes/colors';
import { Spacing } from 'Themes/spacing';
import { Typo } from 'Themes/typo';
import type { UserDisplayModel } from 'User/userDisplayModel';
import { getUserDisplayName } from 'Utils/user';

import { MonitorIcon } from '@libs/shared/ui/icons';

import type { CourseModel } from '../../data';
import { useSelectCourseParticipants, useSelectIsTrainerDesktopLoading } from '../../data';
import { useCourseOrganizationDisplay } from '../../hooks/use-course-organization-display';
import {
  getActiveParticipantsCount,
  getExpectedParticipantsCount,
  getJoinedParticipantsCount,
  isCourseInSession
} from '../../utils/participants-utils';

import { CourseDatesDisplay } from '../dates/course-dates-display';

import { OrganizationTextWithMargin } from './course-shared-styles';
import { CourseTagsDisplay } from './course-tags-display';
import { Description } from './description';
import { LivePreviewCard } from './live-preview-card';
import { MeetingLink } from './meeting-link';
import { ParticipantsInfo } from './participants-info';

import type { DesktopModel } from 'Api';

export const Chip = styled('span')<{ color: string }>`
  background: ${props => props.color};
  padding: 8px;
  height: 25px;
  width: 100px;
  border-radius: 50%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${Colors.white};
`;

const Column = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  min-height: 200px;
  flex: 0 0 auto;
  width: 400px;
`;

const Details = styled(Column)<{ shouldWrapText?: boolean }>`
  align-items: flex-start;
  align-self: flex-start;
  flex-direction: column;
  padding-left: 53px;
  gap: ${Spacing.small};
  flex: 1 1 auto;
  width: auto;
  max-width: none;

  ${({ shouldWrapText }) =>
    shouldWrapText &&
    `
      max-width: 500px;
    `}
`;

const ClickableTitleTypography = styled(Link)`
  color: inherit;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: inherit;
    text-decoration: underline;
  }
`;

const StyledCard = styled(Card)`
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  border: 1px solid ${Colors.lineGrey};
  border-radius: 0;
  padding: 25px 0;
  width: 100%;
`;

const Divider = styled(Box)`
  width: 2px;
  min-height: 100px;
  background: ${Colors.lineGrey};
  flex: 0 0 auto;
`;

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

type Props = {
  course: CourseModel;
  user: UserDisplayModel;
  isOngoing?: boolean;
  shouldSkipHighlighted?: boolean;
};

export const CourseCard = ({
  course,
  user,
  isOngoing = false,
  shouldSkipHighlighted = false
}: Readonly<Props>) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const currentQuery = searchParams.toString();
  const withQuery = (url: string) => (currentQuery ? `${url}?${currentQuery}` : url);

  const isTrainerDesktopLoading = useSelectIsTrainerDesktopLoading(course.id);
  const courseParticipants = useSelectCourseParticipants(course.id);

  const shouldDisplayMyDesktop = !!(
    user &&
    course.flags?.isParticipant &&
    (course.myDesktop ??
      (course.hasFreshStart && !course.flags?.isFinished && !course.flags?.isEnded))
  );

  const shouldDisplayCoursePanel = !!(course.id && !course.flags?.isParticipant);

  const shouldDisplayTrainerPreviewDesktop = !!(
    !isTrainerDesktopLoading &&
    course.flags?.isParticipant &&
    course.whetherTrainerAllowedPreview &&
    course.trainerDesktop &&
    !course.flags?.isFinished
  );

  const shouldDisplayTrainerDesktopMessage = !!(
    course.flags?.isParticipant &&
    (!course.whetherTrainerAllowedPreview || !course.trainerDesktop) &&
    !course.flags?.isFinished
  );

  const shouldDisplayDivider = !!(
    shouldDisplayMyDesktop &&
    (shouldDisplayCoursePanel ||
      shouldDisplayTrainerPreviewDesktop ||
      shouldDisplayTrainerDesktopMessage)
  );

  const shouldDisplayColumns = !!(
    shouldDisplayMyDesktop ||
    shouldDisplayCoursePanel ||
    shouldDisplayTrainerPreviewDesktop ||
    shouldDisplayTrainerDesktopMessage
  );

  const organizationDisplay = useCourseOrganizationDisplay(course);

  const showParticipantsInfo = isCourseInSession(course) && !course.flags?.isParticipant;
  const activeCount = getActiveParticipantsCount(courseParticipants, course);
  const joinedCount = getJoinedParticipantsCount(courseParticipants, course);
  const expectedCount = getExpectedParticipantsCount(course);

  return (
    <StyledCard>
      <Details shouldWrapText={shouldDisplayMyDesktop}>
        {organizationDisplay && (
          <OrganizationTextWithMargin variant="body2">
            {organizationDisplay}
          </OrganizationTextWithMargin>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.small }}>
          {shouldDisplayCoursePanel ? (
            <ClickableTitleTypography
              to={
                course.hasFreshStart || course.flags?.isFinished || !course.isTrainer
                  ? withQuery(urls.coursePage(course.id as string))
                  : withQuery(urls.courseSettingsPage(course.id as string))
              }
            >
              <Typography variant={Typo.title}>{course.title}</Typography>
            </ClickableTitleTypography>
          ) : (
            <Typography variant={Typo.title}>{course.title}</Typography>
          )}
          {showParticipantsInfo && (
            <ParticipantsInfo active={activeCount} joined={joinedCount} expected={expectedCount} />
          )}
        </div>

        {!course.isTest ? (
          <>
            <CourseDatesDisplay dates={course.dates} courseTimezone={course.timezone} />

            <MeetingLink
              meetingLink={course.meetingLink}
              meetingPlatform={course.meetingPlatform}
            />
          </>
        ) : (
          ''
        )}

        <CourseTagsDisplay course={course} />

        <Description value={course.description} shouldShowAllValue={course.isTest} />
      </Details>
      {shouldDisplayColumns ? (
        <Column>
          {shouldDisplayMyDesktop ? (
            <LivePreviewCard
              staticSize
              course={course}
              user={user}
              desktop={course.myDesktop as DesktopModel}
              isTrainerDesktop={course.isTrainer}
              isHighlighted={(isOngoing && !course.isTrainer) || course.isTest}
            />
          ) : (
            ''
          )}
        </Column>
      ) : (
        ''
      )}
      {shouldDisplayDivider ? (
        <>
          <Divider />
        </>
      ) : (
        ''
      )}
      {/* TODO: może jakoś ładniej zrobić te if'y? teraz jest trochę nie czytelny kod */}
      {shouldDisplayColumns ? (
        <Column>
          {isTrainerDesktopLoading && course.whetherTrainerAllowedPreview ? (
            // TODO: loader styles
            <Skeleton
              variant="rounded"
              animation="wave"
              width={300}
              height={220}
              sx={{ ml: '10px', mr: '10px' }}
            />
          ) : shouldDisplayCoursePanel ? (
            <StyledLink
              to={
                course.hasFreshStart || course.flags?.isFinished || !course.isTrainer
                  ? withQuery(urls.coursePage(course.id as string))
                  : withQuery(urls.courseSettingsPage(course.id as string))
              }
            >
              <Button
                variant={
                  isOngoing ||
                  course.isTest ||
                  (!shouldSkipHighlighted && !course.hasFreshStart && !course.flags?.isFinished)
                    ? 'contained'
                    : 'outlined'
                }
                startIcon={<MonitorIcon />}
                sx={{ mr: '10px' }}
              >
                {t('coursesPage.coursePanel')}
              </Button>
            </StyledLink>
          ) : shouldDisplayTrainerPreviewDesktop ? (
            <LivePreviewCard
              staticSize
              course={course}
              user={
                {
                  displayName: getUserDisplayName(course.trainer?.displayName),
                  avatar: course.trainer?.avatar
                } as UserDisplayModel
              }
              desktop={course.trainerDesktop as DesktopModel}
              isTrainerDesktop
              isTrainerDesktopPreview
            />
          ) : shouldDisplayTrainerDesktopMessage ? (
            <Typography textAlign="center" fontStyle="italic" style={{ maxWidth: '200px' }}>
              {t('coursesPage.trainerDidNotMakeHisVirtualComputerAvailable')}
            </Typography>
          ) : (
            ''
          )}
        </Column>
      ) : (
        ''
      )}
    </StyledCard>
  );
};
